import React, { useEffect, useState, useContext, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Box,
  Card,
  Text,
  Input,
  Button,
  Avatar,
  VStack,
  HStack,
  Flex,
  Icon,
} from "@chakra-ui/react";
import ScrollableFeed from "react-scrollable-feed";
import { FaPaperPlane } from "react-icons/fa";
import { CheckIcon } from "@chakra-ui/icons";

import axiosInstance from "../routes/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { SocketContext } from "../context/SocketContext";
import BackButton from "../components/BackButton";
import UserTyping from "../components/UserTyping";
import { ChatTitle, setLsCurrentChat } from "../logics/chatLogic";
import { formatTimeOnly, formatDateForChatHeader } from "../logics/timeLogic";
import ConversationLoadingSkeleton from "../loading/ConversationLoadingSkeleton";
import replyAudioFile from "../sounds/message_reply_tone.ogg";

// Helper function to check if two dates are on different days
const isDifferentDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.toDateString() !== d2.toDateString();
};

const ConversationPage = () => {
  const { currentUser, checkUserAuth } = useAuth();
  const { socket } = useContext(SocketContext);
  const { chatId } = useParams();
  const [messages, setMessages] = useState([]);
  const [chatData, setChatData] = useState({});
  const [messageToSend, setMessageToSend] = useState("");
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [typingUserName, setTypingUserName] = useState("");

  // to get chat history
  const getChatMessages = useCallback(async () => {
    setLoading(true);
    const config = {
      headers: {
        "Content-type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
    };

    try {
      const userChats = await axiosInstance.get(
        `/api/messages/${chatId}`,
        config,
      );
      setMessages(userChats.data.data);
      setChatData(userChats.data.chat);
      setLoading(false);
      socket.emit("JOINED_CHAT_ROOM", chatId, currentUser.id);
    } catch (error) {
      console.log(error.message);
      setLoading(false);
      checkUserAuth(error.status);
    }
  }, [chatId, currentUser.token, currentUser.id, socket, checkUserAuth]);

  // to send a message
  const sendMessageHandler = async () => {
    if (!messageToSend) {
      return false;
    }

    const messagePayload = {
      groupId: chatId,
      message: messageToSend,
    };
    setMessageToSend("");
    setSendingMessage(true);
    const config = {
      headers: {
        "Content-type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
    };
    try {
      const { data } = await axiosInstance.post(
        "/api/messages",
        messagePayload,
        config,
      );
      setMessages((prevMsgs) => [...prevMsgs, data.data]);
      setSendingMessage(false);
    } catch (error) {
      console.log(error.message);
      setSendingMessage(false);
      checkUserAuth(error.status);
    }
  };

  const handleMessageInputChange = (e) => {
    setMessageToSend(e.target.value);
  };

  // to handle enter key event
  const handleEnterKeyPress = (event) => {
    if (event.key === "Enter") {
      sendMessageHandler();
    }
  };

  // for triggering typing event
  const typingHandler = (e) => {
    if (!isUserTyping) {
      // console.log("User typing: ", e.target.value);
      socket.emit("TYPING", currentUser.name, chatData._id, currentUser.id);
      setIsUserTyping(true);
    }

    setTimeout(() => {
      socket.emit("TYPING_STOPPED", chatData._id);
      setIsUserTyping(false);
    }, 2000);
  };

  const playReplyMessageTone = () => {
    const newMessageAudio = new Audio(replyAudioFile);
    newMessageAudio.play().catch((error) => {
      console.error("Error playing incoming call sound:", error);
    });
  };

  const handleNewMessage = useCallback(
    (messageData) => {
      if (
        messageData.sender._id !== currentUser.id &&
        messageData.chatId === chatId
      ) {
        setMessages((prevMsgs) => [...prevMsgs, messageData]);
        playReplyMessageTone();
      }
    },
    [currentUser.id, chatId],
  );

  const setLsChatData = () => {
    setLsCurrentChat(chatData);
  };

  useEffect(() => {
    if (!socket) return;
    getChatMessages();

    // to handle if someone is typing in chat
    socket.on("USER_TYPING", (roomId, name, userId) => {
      if (chatId === roomId) {
        setTypingUserName(name);
      }
    });

    // to handle if typing in stopped
    socket.on("USER_TYPING_STOPPED", (chatId) => {
      setTypingUserName("");
    });

    // to handle new message event
    socket.on("NEW_MESSAGE_TO_CLIENT", handleNewMessage);

    return () => {
      socket.emit("LEAVE_CHAT_ROOM", chatId, currentUser.id);
      socket.off("NEW_MESSAGE_TO_CLIENT", handleNewMessage);
    };
  }, [socket, chatId, currentUser.id, getChatMessages, handleNewMessage]);

  return (
    <Box bg="gray.50" minH="100vh">
      <Flex direction="column" h="100vh" maxW="md" mx="auto">
        {/* Header */}
        <Card
          bg="white"
          borderRadius="none"
          borderBottom="1px solid"
          borderColor="gray.200"
          p={3}
          boxShadow="sm"
        >
          <HStack spacing={3} align="center">
            <BackButton />
            {chatData._id && (
              <>
                <Avatar
                  name={ChatTitle(chatData, currentUser)}
                  size="sm"
                  fontSize="sm"
                  fontWeight="600"
                />
                <VStack spacing={0} align="start" justify="center">
                  <Link
                    onClick={setLsChatData}
                    to={`/chats/${chatData._id}/edit`}
                    style={{ textDecoration: "none" }}
                  >
                    <Text
                      fontSize="md"
                      fontWeight="600"
                      color="gray.800"
                      _hover={{ color: "teal.500" }}
                      lineHeight="1.2"
                    >
                      {ChatTitle(chatData, currentUser)}
                    </Text>
                  </Link>
                  <UserTyping
                    isGroup={chatData.isGroup}
                    typingUserName={typingUserName}
                  />
                </VStack>
              </>
            )}
          </HStack>
        </Card>

        {/* Messages Area */}
        <Card
          flex="1"
          bg="white"
          borderRadius="none"
          borderLeft="1px solid"
          borderRight="1px solid"
          borderColor="gray.200"
          overflowY="auto"
          pb={2}
          boxShadow="none"
        >
          {messages.length ? (
            <ScrollableFeed>
              <VStack spacing={3} align="stretch">
                {messages.map((msg, index) => {
                  const isCurrentUser = msg.sender._id === currentUser.id;
                  const showAvatar =
                    !isCurrentUser &&
                    (index === 0 ||
                      messages[index - 1].sender._id !== msg.sender._id);
                  const showDateHeader =
                    index === 0 ||
                    isDifferentDay(
                      messages[index - 1].createdAt,
                      msg.createdAt,
                    );

                  return (
                    <React.Fragment key={msg._id}>
                      {/* Date Header */}
                      {showDateHeader && (
                        <Flex justify="center" my={4}>
                          <Box
                            bg="gray.100"
                            px={3}
                            py={1}
                            borderRadius="full"
                            boxShadow="sm"
                          >
                            <Text
                              fontSize="xs"
                              color="gray.600"
                              fontWeight="500"
                            >
                              {formatDateForChatHeader(msg.createdAt)}
                            </Text>
                          </Box>
                        </Flex>
                      )}

                      {/* Message */}
                      <HStack
                        align="flex-start"
                        justify={isCurrentUser ? "flex-end" : "flex-start"}
                        spacing={2}
                        px={3}
                      >
                        {!isCurrentUser && (
                          <Avatar
                            size="xs"
                            name={msg.sender.name}
                            visibility={showAvatar ? "visible" : "hidden"}
                          />
                        )}

                        <VStack
                          align={isCurrentUser ? "flex-end" : "flex-start"}
                          spacing={1}
                          maxW="70%"
                        >
                          {!isCurrentUser && showAvatar && (
                            <Text
                              fontSize="xs"
                              color="gray.500"
                              fontWeight="500"
                            >
                              {msg.sender.name}
                            </Text>
                          )}

                          <Box
                            bg={isCurrentUser ? "teal.500" : "white"}
                            color={isCurrentUser ? "white" : "gray.800"}
                            borderRadius="lg"
                            px={4}
                            py={2}
                            boxShadow="sm"
                            border={!isCurrentUser ? "1px solid" : "none"}
                            borderColor="gray.200"
                            maxW="100%"
                            wordBreak="break-word"
                          >
                            <Text fontSize="sm" lineHeight="1.4" mb={1}>
                              {msg.message}
                            </Text>
                            <HStack
                              spacing={1}
                              justify="flex-end"
                              align="center"
                              w="100%"
                            >
                              <Text
                                fontSize="xs"
                                color={
                                  isCurrentUser ? "whiteAlpha.700" : "gray.400"
                                }
                                fontWeight="400"
                                textAlign="right"
                              >
                                {formatTimeOnly(msg.createdAt)}
                              </Text>
                              {isCurrentUser &&
                                msg.readBy &&
                                msg.readBy.some(
                                  (uid) => uid !== currentUser.id,
                                ) && (
                                  <Icon
                                    as={CheckIcon}
                                    color={
                                      isCurrentUser
                                        ? "whiteAlpha.800"
                                        : "teal.500"
                                    }
                                    boxSize={3}
                                  />
                                )}
                            </HStack>
                          </Box>
                        </VStack>
                      </HStack>
                    </React.Fragment>
                  );
                })}
              </VStack>
            </ScrollableFeed>
          ) : loading ? (
            <ConversationLoadingSkeleton />
          ) : (
            <Flex align="center" justify="center" h="full">
              <VStack spacing={4} textAlign="center">
                <Text fontSize="lg" color="gray.500" fontWeight="500">
                  No messages yet
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Say hello to start the conversation
                </Text>
              </VStack>
            </Flex>
          )}
        </Card>

        {/* Message Input */}
        <Card
          bg="white"
          borderRadius="none"
          borderTop="1px solid"
          borderColor="gray.200"
          p={4}
          boxShadow="sm"
        >
          <HStack spacing={3}>
            <Input
              value={messageToSend}
              onKeyUp={handleEnterKeyPress}
              onChange={(e) => {
                typingHandler(e);
                handleMessageInputChange(e);
              }}
              placeholder="Type your message..."
              bg="gray.50"
              border="1px solid"
              borderColor="gray.300"
              borderRadius="full"
              px={4}
              py={2}
              flex="1"
              _focus={{
                borderColor: "teal.400",
                boxShadow: "0 0 0 1px teal.400",
                bg: "white",
              }}
              _hover={{ borderColor: "gray.400" }}
            />
            <Button
              colorScheme="teal"
              leftIcon={<FaPaperPlane />}
              isDisabled={sendingMessage || !messageToSend.trim()}
              isLoading={sendingMessage}
              onClick={sendMessageHandler}
              borderRadius="full"
              px={6}
              fontWeight="600"
            >
              Send
            </Button>
          </HStack>
        </Card>
      </Flex>
    </Box>
  );
};

export default ConversationPage;
